import type {
  AdvancementRule,
  PlayoffBranch,
  PlayoffMatch,
  PlayoffBracketResult,
  ResolvedTeam,
  GroupResult,
} from '@/types/playoff';
import { t } from '@/plugins/i18n';

// ═══════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════

export function parsePositions(posStr: string): number[] {
  if (!posStr) return [];
  const result: number[] = [];
  for (const part of posStr.split(',')) {
    const trimmed = part.trim();
    if (trimmed.includes('-')) {
      const [start, end] = trimmed.split('-').map(Number);
      if (!isNaN(start) && !isNaN(end)) {
        for (let i = start; i <= end; i++) result.push(i);
      }
    } else {
      const num = Number(trimmed);
      if (!isNaN(num)) result.push(num);
    }
  }
  return result;
}

function allGroupLetters(groupCount: number): string[] {
  return Array.from({ length: groupCount }, (_, i) => String.fromCharCode(65 + i));
}

function getApplicableGroups(rule: AdvancementRule, groupCount: number): string[] {
  if (rule.source.group_scope === 'specific_groups' && rule.source.specific_groups?.length) {
    return rule.source.specific_groups;
  }
  return allGroupLetters(groupCount);
}

// ═══════════════════════════════════════════════════════
// Team resolution — real or placeholder
// ═══════════════════════════════════════════════════════

function resolveTeam(
  groupLetter: string,
  position: number,
  role: 'bye' | 'prelim',
  seed: number,
  groupResults: GroupResult[] | null,
): ResolvedTeam {
  if (!groupResults) {
    return {
      id: null,
      name: `${groupLetter}-${position}`,
      seed,
      group: groupLetter,
      group_position: position,
      role,
      is_placeholder: true,
    };
  }
  const result = groupResults.find(
    r => r.groupLetter === groupLetter && r.position === position
  );
  if (!result) {
    return {
      id: null,
      name: `${groupLetter}-${position}`,
      seed,
      group: groupLetter,
      group_position: position,
      role,
      is_placeholder: true,
    };
  }
  return {
    id: result.teamId,
    name: result.teamName,
    logo: result.teamLogo,
    seed,
    group: groupLetter,
    group_position: position,
    role,
    is_placeholder: false,
  };
}

function resolveTeamsByRule(
  rule: AdvancementRule,
  groupCount: number,
  groupResults: GroupResult[] | null,
): ResolvedTeam[] {
  const positions = parsePositions(rule.source.positions);
  const groups = getApplicableGroups(rule, groupCount);
  const role = (rule.target.role === 'bye' || rule.target.role === 'prelim')
    ? rule.target.role
    : 'prelim' as const;

  const teams: (ResolvedTeam & { points: number })[] = [];

  for (const pos of positions) {
    for (const g of groups) {
      const gr = groupResults?.find(r => r.groupLetter === g && r.position === pos);
      teams.push({
        ...resolveTeam(g, pos, role, 0, groupResults),
        points: gr?.points ?? 0,
      });
    }
  }

  // Sort by seeding_method
  if (rule.target.seeding_method === 'group_order') {
    teams.sort((a, b) => {
      if (a.group_position !== b.group_position) return a.group_position - b.group_position;
      return a.group.localeCompare(b.group);
    });
  } else if (rule.target.seeding_method === 'points') {
    teams.sort((a, b) => b.points - a.points);
  }
  // 'cross_rank' — same as group_order for seeding purposes

  teams.forEach((t, i) => { t.seed = i + 1; });
  return teams;
}

// ═══════════════════════════════════════════════════════
// Cross-group-pairs bracket generation (Warriors Cup)
// ═══════════════════════════════════════════════════════

function generateCrossGroupPairsBracket(
  branch: PlayoffBranch,
  byeRule: AdvancementRule,
  prelimRule: AdvancementRule,
  groupCount: number,
  groupResults: GroupResult[] | null,
): PlayoffMatch[] {
  const matches: PlayoffMatch[] = [];
  const pairs = prelimRule.pairing?.group_pairs || byeRule.pairing?.group_pairs || [];

  const byeTeams = resolveTeamsByRule(byeRule, groupCount, groupResults);
  let matchIdx = 0;

  // Step 1: Generate preliminary matches from group pairs
  // Each pair produces 2 prelim matches:
  //   2.X vs 3.Y → winner plays 1.X
  //   3.X vs 2.Y → winner plays 1.Y
  const prelimMatchIds: { matchId: string; byeGroup: string }[] = [];

  for (const pair of pairs) {
    const ga = pair.group_a;
    const gb = pair.group_b;

    // PK: 2.ga vs 3.gb → winner plays 1.ga
    const pk1Id = `pk_${matchIdx + 1}`;
    const team2a = resolveTeam(ga, 2, 'prelim', 0, groupResults);
    const team3b = resolveTeam(gb, 3, 'prelim', 0, groupResults);
    matches.push({
      id: pk1Id,
      round_key: 'preliminary',
      round_name: t('bracket.preliminary', { n: matchIdx + 1 }),
      team_a: team2a,
      team_b: team3b,
      series: branch.preliminary_series,
      winner_to: null, // linked below
      loser_to: null,  // linked below
      is_placeholder: !groupResults,
      branch_id: branch.id,
    });
    prelimMatchIds.push({ matchId: pk1Id, byeGroup: ga });
    matchIdx++;

    // PK: 3.ga vs 2.gb → winner plays 1.gb
    const pk2Id = `pk_${matchIdx + 1}`;
    const team3a = resolveTeam(ga, 3, 'prelim', 0, groupResults);
    const team2b = resolveTeam(gb, 2, 'prelim', 0, groupResults);
    matches.push({
      id: pk2Id,
      round_key: 'preliminary',
      round_name: t('bracket.preliminary', { n: matchIdx + 1 }),
      team_a: team3a,
      team_b: team2b,
      series: branch.preliminary_series,
      winner_to: null,
      loser_to: null,
      is_placeholder: !groupResults,
      branch_id: branch.id,
    });
    prelimMatchIds.push({ matchId: pk2Id, byeGroup: gb });
    matchIdx++;
  }

  // Step 2: Generate quarterfinals — BYE teams paired with prelim winners
  const qfMatches: PlayoffMatch[] = [];
  for (let i = 0; i < prelimMatchIds.length; i++) {
    const { matchId: pkMatchId, byeGroup } = prelimMatchIds[i];
    const byeTeam = byeTeams.find(t => t.group === byeGroup) || null;

    const qfId = `qf_${i + 1}`;
    const winnerPlaceholder: ResolvedTeam = {
      id: null,
      name: t('bracket.winnerOf', { name: matches.find(m => m.id === pkMatchId)?.round_name || pkMatchId }),
      seed: 0,
      group: '',
      group_position: 0,
      role: 'prelim',
      is_placeholder: true,
    };

    qfMatches.push({
      id: qfId,
      round_key: 'quarterfinal',
      round_name: `${branch.rounds[0]?.name || t('bracket.quarterfinal')} ${i + 1}`,
      team_a: byeTeam,
      team_b: winnerPlaceholder,
      series: branch.rounds[0]?.series || 1,
      winner_to: null,
      loser_to: null,
      is_placeholder: true,
      branch_id: branch.id,
    });

    // Link PK winner_to → QF
    const pkMatch = matches.find(m => m.id === pkMatchId);
    if (pkMatch) pkMatch.winner_to = qfId;
  }

  matches.push(...qfMatches);

  // Step 3: Generate remaining rounds (SF, Final, etc.)
  let prevRoundMatches = qfMatches;
  for (let ri = 1; ri < branch.rounds.length; ri++) {
    const round = branch.rounds[ri];
    const nextRoundMatches: PlayoffMatch[] = [];

    for (let mi = 0; mi < Math.ceil(prevRoundMatches.length / 2); mi++) {
      const roundKey = ri === branch.rounds.length - 1 ? 'final'
        : ri === branch.rounds.length - 2 ? 'semifinal'
        : `round_${ri + 1}`;

      const matchId = roundKey === 'final' ? 'final' : `${roundKey}_${mi + 1}`;

      const teamAPlaceholder: ResolvedTeam = {
        id: null,
        name: t('bracket.winnerOf', { name: prevRoundMatches[mi * 2]?.round_name || '?' }),
        seed: 0, group: '', group_position: 0,
        role: 'prelim', is_placeholder: true,
      };
      const teamBPlaceholder: ResolvedTeam = {
        id: null,
        name: prevRoundMatches[mi * 2 + 1]
          ? t('bracket.winnerOf', { name: prevRoundMatches[mi * 2 + 1].round_name })
          : 'BYE',
        seed: 0, group: '', group_position: 0,
        role: 'prelim', is_placeholder: true,
      };

      nextRoundMatches.push({
        id: matchId,
        round_key: roundKey,
        round_name: branch.rounds.length === 1
          ? round.name
          : `${round.name}${Math.ceil(prevRoundMatches.length / 2) > 1 ? ` ${mi + 1}` : ''}`,
        team_a: teamAPlaceholder,
        team_b: prevRoundMatches[mi * 2 + 1] ? teamBPlaceholder : null,
        series: round.series,
        winner_to: null,
        loser_to: null,
        is_placeholder: true,
        branch_id: branch.id,
      });

      // Link prev round → this round
      if (prevRoundMatches[mi * 2]) prevRoundMatches[mi * 2].winner_to = matchId;
      if (prevRoundMatches[mi * 2 + 1]) prevRoundMatches[mi * 2 + 1].winner_to = matchId;
    }

    matches.push(...nextRoundMatches);
    prevRoundMatches = nextRoundMatches;
  }

  // Link preliminary losers
  const prelimMatches = matches.filter(m => m.round_key === 'preliminary');
  for (const pm of prelimMatches) {
    // Losers go to next branch that accepts prelim losers (branch_X_prelim_losers)
    pm.loser_to = `branch_${branch.id}_prelim_losers`;
  }

  // Link SF losers for placement matches
  const sfMatches = matches.filter(m => m.round_key === 'semifinal');
  for (const sf of sfMatches) {
    sf.loser_to = `branch_${branch.id}_sf_losers`;
  }

  const finalMatch = matches.find(m => m.round_key === 'final');
  if (finalMatch) {
    finalMatch.loser_to = `branch_${branch.id}_final_loser`;
  }

  return matches;
}

// ═══════════════════════════════════════════════════════
// Mirror / sequential / same_group bracket generation
// ═══════════════════════════════════════════════════════

function generateStandardBracket(
  branch: PlayoffBranch,
  branchRules: AdvancementRule[],
  groupCount: number,
  groupResults: GroupResult[] | null,
): PlayoffMatch[] {
  const matches: PlayoffMatch[] = [];
  const byeRule = branchRules.find(r => r.target.role === 'bye');
  const prelimRule = branchRules.find(r => r.target.role === 'prelim');

  const byeTeams = byeRule ? resolveTeamsByRule(byeRule, groupCount, groupResults) : [];
  const prelimTeams = prelimRule ? resolveTeamsByRule(prelimRule, groupCount, groupResults) : [];
  const pairingType = prelimRule?.pairing?.type || byeRule?.pairing?.type || 'mirror';

  // Step 1: Prelim matches
  if (branch.preliminary_rounds > 0 && prelimTeams.length >= 2) {
    const sorted = [...prelimTeams];
    const prelims: PlayoffMatch[] = [];

    if (pairingType === 'same_group') {
      // Same group: pair prelim teams from same group together
      // e.g. 2.A vs 3.A, 2.B vs 3.B — grouped by group letter
      const groupMap = new Map<string, ResolvedTeam[]>();
      for (const t of sorted) {
        if (!groupMap.has(t.group)) groupMap.set(t.group, []);
        groupMap.get(t.group)!.push(t);
      }
      for (const [, gTeams] of groupMap) {
        // Sort by position within group (lower position = stronger)
        gTeams.sort((a, b) => a.group_position - b.group_position);
        for (let i = 0; i < gTeams.length - 1; i += 2) {
          prelims.push({
            id: `pk_${prelims.length + 1}`,
            round_key: 'preliminary',
            round_name: t('bracket.preliminary', { n: prelims.length + 1 }),
            team_a: gTeams[i],
            team_b: gTeams[i + 1] || null,
            series: branch.preliminary_series,
            winner_to: null,
            loser_to: `branch_${branch.id}_prelim_losers`,
            is_placeholder: !groupResults,
            branch_id: branch.id,
          });
        }
      }
    } else if (pairingType === 'mirror') {
      // Mirror seeding: best prelim vs worst prelim
      // Seeds are already assigned by resolveTeamsByRule
      // Pair: seed P+1 vs seed 2P, seed P+2 vs seed 2P-1, etc.
      const half = Math.floor(sorted.length / 2);
      for (let i = 0; i < half; i++) {
        prelims.push({
          id: `pk_${i + 1}`,
          round_key: 'preliminary',
          round_name: t('bracket.preliminary', { n: i + 1 }),
          team_a: sorted[i],
          team_b: sorted[sorted.length - 1 - i],
          series: branch.preliminary_series,
          winner_to: null,
          loser_to: `branch_${branch.id}_prelim_losers`,
          is_placeholder: !groupResults,
          branch_id: branch.id,
        });
      }
    } else {
      // Sequential: pair in order (1v2, 3v4, ...)
      for (let i = 0; i < sorted.length - 1; i += 2) {
        prelims.push({
          id: `pk_${Math.floor(i / 2) + 1}`,
          round_key: 'preliminary',
          round_name: t('bracket.preliminary', { n: Math.floor(i / 2) + 1 }),
          team_a: sorted[i],
          team_b: sorted[i + 1] || null,
          series: branch.preliminary_series,
          winner_to: null,
          loser_to: `branch_${branch.id}_prelim_losers`,
          is_placeholder: !groupResults,
          branch_id: branch.id,
        });
      }
    }

    matches.push(...prelims);

    // Step 2: First main round (QF) — pair BYE teams with prelim winners
    const roundName = branch.rounds[0]?.name || t('bracket.quarterfinal');
    const qfMatches: PlayoffMatch[] = [];

    if (pairingType === 'same_group') {
      // Same group: BYE team (1st in group X) waits for winner of PK from group X
      for (let i = 0; i < byeTeams.length; i++) {
        const bye = byeTeams[i];
        // Find prelim match where both teams are from the same group as the BYE team
        const linkedPrelimIdx = prelims.findIndex(pk =>
          pk.team_a?.group === bye.group || pk.team_b?.group === bye.group
        );
        const linkedPrelim = linkedPrelimIdx >= 0 ? prelims[linkedPrelimIdx] : prelims[i];

        const qfId = `qf_${i + 1}`;
        qfMatches.push({
          id: qfId,
          round_key: 'quarterfinal',
          round_name: `${roundName} ${i + 1}`,
          team_a: bye,
          team_b: {
            id: null,
            name: t('bracket.winnerOf', { name: linkedPrelim?.round_name || '?' }),
            seed: 0, group: '', group_position: 0,
            role: 'prelim', is_placeholder: true,
          },
          series: branch.rounds[0]?.series || 1,
          winner_to: null,
          loser_to: null,
          is_placeholder: true,
          branch_id: branch.id,
        });
        if (linkedPrelim) linkedPrelim.winner_to = qfId;
      }
    } else if (pairingType === 'mirror') {
      // Mirror: Best BYE (seed 1) plays winner of weakest PK (last prelim)
      // Seed 2 plays winner of second weakest, etc.
      for (let i = 0; i < byeTeams.length; i++) {
        const linkedPrelimIdx = prelims.length - 1 - i;
        const linkedPrelim = prelims[linkedPrelimIdx] || prelims[i];

        const qfId = `qf_${i + 1}`;
        qfMatches.push({
          id: qfId,
          round_key: 'quarterfinal',
          round_name: `${roundName} ${i + 1}`,
          team_a: byeTeams[i],
          team_b: {
            id: null,
            name: t('bracket.winnerOf', { name: linkedPrelim?.round_name || '?' }),
            seed: 0, group: '', group_position: 0,
            role: 'prelim', is_placeholder: true,
          },
          series: branch.rounds[0]?.series || 1,
          winner_to: null,
          loser_to: null,
          is_placeholder: true,
          branch_id: branch.id,
        });
        if (linkedPrelim) linkedPrelim.winner_to = qfId;
      }
    } else {
      // Sequential: BYE 1 → PK1 winner, BYE 2 → PK2 winner, ...
      for (let i = 0; i < byeTeams.length; i++) {
        const linkedPrelim = prelims[i];
        const qfId = `qf_${i + 1}`;
        qfMatches.push({
          id: qfId,
          round_key: 'quarterfinal',
          round_name: `${roundName} ${i + 1}`,
          team_a: byeTeams[i],
          team_b: {
            id: null,
            name: t('bracket.winnerOf', { name: linkedPrelim?.round_name || '?' }),
            seed: 0, group: '', group_position: 0,
            role: 'prelim', is_placeholder: true,
          },
          series: branch.rounds[0]?.series || 1,
          winner_to: null,
          loser_to: null,
          is_placeholder: true,
          branch_id: branch.id,
        });
        if (linkedPrelim) linkedPrelim.winner_to = qfId;
      }
    }

    matches.push(...qfMatches);

    // Step 3: Remaining rounds
    let prevRoundMatches = qfMatches;
    for (let ri = 1; ri < branch.rounds.length; ri++) {
      const round = branch.rounds[ri];
      const nextRound: PlayoffMatch[] = [];

      for (let mi = 0; mi < Math.ceil(prevRoundMatches.length / 2); mi++) {
        const roundKey = ri === branch.rounds.length - 1 ? 'final'
          : ri === branch.rounds.length - 2 ? 'semifinal'
          : `round_${ri + 1}`;
        const matchId = roundKey === 'final' ? 'final' : `${roundKey}_${mi + 1}`;

        nextRound.push({
          id: matchId,
          round_key: roundKey,
          round_name: `${round.name}${Math.ceil(prevRoundMatches.length / 2) > 1 ? ` ${mi + 1}` : ''}`,
          team_a: {
            id: null, name: t('bracket.winnerOf', { name: prevRoundMatches[mi * 2]?.round_name || '?' }),
            seed: 0, group: '', group_position: 0, role: 'prelim', is_placeholder: true,
          },
          team_b: prevRoundMatches[mi * 2 + 1] ? {
            id: null, name: t('bracket.winnerOf', { name: prevRoundMatches[mi * 2 + 1].round_name }),
            seed: 0, group: '', group_position: 0, role: 'prelim', is_placeholder: true,
          } : null,
          series: round.series,
          winner_to: null,
          loser_to: null,
          is_placeholder: true,
          branch_id: branch.id,
        });

        if (prevRoundMatches[mi * 2]) prevRoundMatches[mi * 2].winner_to = matchId;
        if (prevRoundMatches[mi * 2 + 1]) prevRoundMatches[mi * 2 + 1].winner_to = matchId;
      }

      matches.push(...nextRound);
      prevRoundMatches = nextRound;
    }

    // Link SF/final losers
    for (const m of matches.filter(m => m.round_key === 'semifinal')) {
      m.loser_to = `branch_${branch.id}_sf_losers`;
    }
    const finalM = matches.find(m => m.round_key === 'final');
    if (finalM) finalM.loser_to = `branch_${branch.id}_final_loser`;

  } else {
    // No preliminary rounds — direct bracket from all teams
    const allTeams = [...byeTeams, ...prelimTeams];
    allTeams.forEach((t, i) => { t.seed = i + 1; });

    // Build single-elimination bracket
    let currentRoundTeams: (ResolvedTeam | null)[] = allTeams;

    for (let ri = 0; ri < branch.rounds.length; ri++) {
      const round = branch.rounds[ri];
      const roundMatches: PlayoffMatch[] = [];
      const matchCount = Math.ceil(currentRoundTeams.length / 2);

      const roundKey = ri === branch.rounds.length - 1 ? 'final'
        : ri === branch.rounds.length - 2 ? 'semifinal'
        : `round_${ri + 1}`;

      for (let mi = 0; mi < matchCount; mi++) {
        const matchId = roundKey === 'final' ? 'final' : `${roundKey}_${mi + 1}`;
        roundMatches.push({
          id: matchId,
          round_key: roundKey,
          round_name: `${round.name}${matchCount > 1 ? ` ${mi + 1}` : ''}`,
          team_a: currentRoundTeams[mi * 2] || null,
          team_b: currentRoundTeams[mi * 2 + 1] || null,
          series: round.series,
          winner_to: null,
          loser_to: null,
          is_placeholder: !groupResults,
          branch_id: branch.id,
        });
      }

      // Link from previous round
      if (ri > 0) {
        const prevMatches = matches.filter(m => {
          const prevKey = ri - 1 === branch.rounds.length - 2 ? 'semifinal'
            : ri - 1 === branch.rounds.length - 1 ? 'final'
            : `round_${ri}`;
          return m.round_key === prevKey;
        });
        for (let pi = 0; pi < prevMatches.length; pi++) {
          const targetMatch = roundMatches[Math.floor(pi / 2)];
          if (targetMatch) prevMatches[pi].winner_to = targetMatch.id;
        }
      }

      matches.push(...roundMatches);
      currentRoundTeams = roundMatches.map(() => null); // placeholders for next round
    }

    // Link losers
    for (const m of matches.filter(m => m.round_key === 'semifinal')) {
      m.loser_to = `branch_${branch.id}_sf_losers`;
    }
    const finalM = matches.find(m => m.round_key === 'final');
    if (finalM) finalM.loser_to = `branch_${branch.id}_final_loser`;
  }

  return matches;
}

// ═══════════════════════════════════════════════════════
// Direct playoff bracket (no groups, no advancement rules)
// ═══════════════════════════════════════════════════════

export function autoRounds(teamsAfterPrelim: number, branchRounds: { name: string; series: number }[]): { name: string; series: number }[] {
  const n = Math.max(1, Math.ceil(Math.log2(teamsAfterPrelim)));
  const rounds: { name: string; series: number }[] = [];
  for (let i = 0; i < n; i++) {
    const fromEnd = n - 1 - i;
    let name: string;
    if (fromEnd === 0) name = t('bracket.final');
    else if (fromEnd === 1) name = t('bracket.semifinal');
    else if (fromEnd === 2) name = t('bracket.quarterfinal');
    else if (fromEnd === 3) name = t('bracket.roundOf16');
    else name = t('bracket.roundN', { n: i + 1 });
    // Preserve user-configured series if available
    rounds.push({ name, series: branchRounds[i]?.series || 1 });
  }
  return rounds;
}

function roundKey(ri: number, total: number): string {
  const fromEnd = total - 1 - ri;
  if (fromEnd === 0) return 'final';
  if (fromEnd === 1) return 'semifinal';
  if (fromEnd === 2) return 'quarterfinal';
  return `round_${ri + 1}`;
}

function buildRemainingRounds(
  prevRound: PlayoffMatch[],
  rounds: { name: string; series: number }[],
  startIndex: number,
  branchId: number,
): PlayoffMatch[] {
  const matches: PlayoffMatch[] = [];
  let prev = prevRound;

  for (let ri = startIndex; ri < rounds.length; ri++) {
    const round = rounds[ri];
    const nextRound: PlayoffMatch[] = [];
    const matchCount = Math.ceil(prev.length / 2);
    const rk = roundKey(ri, rounds.length);

    for (let mi = 0; mi < matchCount; mi++) {
      const matchId = rk === 'final' ? 'final' : `${rk}_${mi + 1}`;
      nextRound.push({
        id: matchId,
        round_key: rk,
        round_name: `${round.name}${matchCount > 1 ? ` ${mi + 1}` : ''}`,
        team_a: {
          id: null, name: t('bracket.winnerOf', { name: prev[mi * 2]?.round_name || '?' }),
          seed: 0, group: '', group_position: 0, role: 'prelim', is_placeholder: true,
        },
        team_b: prev[mi * 2 + 1] ? {
          id: null, name: t('bracket.winnerOf', { name: prev[mi * 2 + 1].round_name }),
          seed: 0, group: '', group_position: 0, role: 'prelim', is_placeholder: true,
        } : null,
        series: round.series,
        winner_to: null,
        loser_to: null,
        is_placeholder: true,
        branch_id: branchId,
      });
      if (prev[mi * 2]) prev[mi * 2].winner_to = matchId;
      if (prev[mi * 2 + 1]) prev[mi * 2 + 1].winner_to = matchId;
    }
    matches.push(...nextRound);
    prev = nextRound;
  }
  return matches;
}

function linkLosers(matches: PlayoffMatch[], branchId: number): void {
  for (const m of matches.filter(m => m.round_key === 'semifinal')) {
    m.loser_to = `branch_${branchId}_sf_losers`;
  }
  const f = matches.find(m => m.round_key === 'final');
  if (f) f.loser_to = `branch_${branchId}_final_loser`;
}

function getLosersLabel(teamsFrom: string): string {
  if (teamsFrom.startsWith('prelim_losers')) return t('bracket.loserPreliminary');
  if (teamsFrom.startsWith('qf_losers')) return t('bracket.loserQuarterfinal');
  if (teamsFrom.startsWith('sf_losers')) return t('bracket.loserSemifinal');
  return '';
}

function generateDirectBracket(branch: PlayoffBranch, groupCount: number = 0): PlayoffMatch[] {
  const matches: PlayoffMatch[] = [];
  const tc = branch.team_count;

  // Auto-generate round structure from team count
  const teamsAfterPrelim = branch.preliminary_rounds > 0 ? tc - branch.preliminary_rounds : tc;
  const rounds = autoRounds(teamsAfterPrelim, branch.rounds);

  // ═══ Cross-pairing with groups (skip for losers branches) ═══
  const losersLabel = getLosersLabel(branch.teams_from);
  if (groupCount >= 2 && !losersLabel) {
    const teamsPerGroup = Math.floor(tc / groupCount);
    const groups = Array.from({ length: groupCount }, (_, i) => String.fromCharCode(65 + i));

    // Auto-pair groups: (A,B), (C,D), ...
    const gPairs: [string, string][] = [];
    for (let i = 0; i < groups.length - 1; i += 2) {
      gPairs.push([groups[i], groups[i + 1]]);
    }

    if (branch.preliminary_rounds > 0) {
      // ─── With prelims: weaker teams play prelim, strong get BYE ───
    // Pair (X,Y): X-2 vs Y-3 (strong), X-3 vs Y-2 (weak)
    const pksByPair: PlayoffMatch[][] = gPairs.map(() => []);
    for (let pi = 0; pi < gPairs.length; pi++) {
      const [g1, g2] = gPairs[pi];
      const nPrelimPos = teamsPerGroup - 1;
      for (let i = 0; i < nPrelimPos; i++) {
        const posA = i + 2;
        const posB = teamsPerGroup - i;
        pksByPair[pi].push({
          id: '',
          round_key: 'preliminary',
          round_name: '',
          team_a: {
            id: null, name: `${g1}-${posA}`, seed: 0,
            group: g1, group_position: posA, role: 'prelim', is_placeholder: true,
          },
          team_b: {
            id: null, name: `${g2}-${posB}`, seed: 0,
            group: g2, group_position: posB, role: 'prelim', is_placeholder: true,
          },
          series: branch.preliminary_series,
          winner_to: null,
          loser_to: `branch_${branch.id}_prelim_losers`,
          is_placeholder: true,
          branch_id: branch.id,
        });
      }
    }

    // Order prelims: by strength level, within each level reversed pair order
    const maxLevel = Math.max(...pksByPair.map(a => a.length));
    const orderedPrelims: PlayoffMatch[] = [];
    for (let level = 0; level < maxLevel; level++) {
      for (let pi = gPairs.length - 1; pi >= 0; pi--) {
        if (level < pksByPair[pi].length) {
          const pk = pksByPair[pi][level];
          pk.id = `pk_${orderedPrelims.length + 1}`;
          pk.round_name = t('bracket.preliminary', { n: orderedPrelims.length + 1 });
          orderedPrelims.push(pk);
        }
      }
    }
    matches.push(...orderedPrelims);

    // QF: BYE from pairX plays PK winner from OTHER pair at same level
    const qfRoundName = rounds[0]?.name || t('bracket.quarterfinal');
    const firstRk = roundKey(0, rounds.length);
    const qfMatches: PlayoffMatch[] = [];

    for (let level = 0; level < maxLevel; level++) {
      for (let pi = gPairs.length - 1; pi >= 0; pi--) {
        const byeGroup = gPairs[pi][level];
        if (!byeGroup) continue;
        const bye: ResolvedTeam = {
          id: null, name: `${byeGroup}-1`, seed: 0,
          group: byeGroup, group_position: 1, role: 'bye', is_placeholder: true,
        };
        // Cross: play PK winner from OTHER pair at same level
        const otherPi = gPairs.length === 2 ? (1 - pi) : ((pi + 1) % gPairs.length);
        const linkedPK = pksByPair[otherPi]?.[level];

        const qfId = `qf_${qfMatches.length + 1}`;
        qfMatches.push({
          id: qfId,
          round_key: firstRk,
          round_name: `${qfRoundName} ${qfMatches.length + 1}`,
          team_a: bye,
          team_b: {
            id: null,
            name: t('bracket.winnerOf', { name: linkedPK?.round_name || '?' }),
            seed: 0, group: '', group_position: 0,
            role: 'prelim', is_placeholder: true,
          },
          series: rounds[0]?.series || 1,
          winner_to: null, loser_to: null,
          is_placeholder: true,
          branch_id: branch.id,
        });
        if (linkedPK) linkedPK.winner_to = qfId;
      }
    }
    matches.push(...qfMatches);

    // Remaining rounds (SF, Final, ...) — auto-generated
    matches.push(...buildRemainingRounds(qfMatches, rounds, 1, branch.id));
    linkLosers(matches, branch.id);
    return matches;

    } else {
      // ─── No prelims: all teams play in first round, cross-paired ───
      // Within each pair (X,Y): X-1 vs Y-pos_last, X-2 vs Y-pos_last-1, etc.
      const firstRk = roundKey(0, rounds.length);
      const firstRoundName = rounds[0]?.name || t('bracket.quarterfinal');
      const firstRoundMatches: PlayoffMatch[] = [];

      for (let pi = gPairs.length - 1; pi >= 0; pi--) {
        const [g1, g2] = gPairs[pi];
        for (let i = 0; i < teamsPerGroup; i++) {
          const posA = i + 1;
          const posB = teamsPerGroup - i;
          const matchId = `${firstRk}_${firstRoundMatches.length + 1}`;
          firstRoundMatches.push({
            id: matchId === 'final_1' ? 'final' : matchId,
            round_key: firstRk,
            round_name: `${firstRoundName} ${firstRoundMatches.length + 1}`,
            team_a: {
              id: null, name: `${g1}-${posA}`, seed: 0,
              group: g1, group_position: posA, role: 'bye', is_placeholder: true,
            },
            team_b: {
              id: null, name: `${g2}-${posB}`, seed: 0,
              group: g2, group_position: posB, role: 'bye', is_placeholder: true,
            },
            series: rounds[0]?.series || 1,
            winner_to: null, loser_to: null,
            is_placeholder: true,
            branch_id: branch.id,
          });
        }
      }
      matches.push(...firstRoundMatches);
      matches.push(...buildRemainingRounds(firstRoundMatches, rounds, 1, branch.id));
      linkLosers(matches, branch.id);
      return matches;
    }
  }

  // ═══ No groups: seed-based bracket ═══
  const allTeams: ResolvedTeam[] = Array.from({ length: tc }, (_, i) => ({
    id: null,
    name: losersLabel ? `${losersLabel} ${i + 1}` : `Seed ${i + 1}`,
    seed: i + 1,
    group: '',
    group_position: 0,
    role: 'bye' as const,
    is_placeholder: true,
  }));

  if (branch.preliminary_rounds > 0) {
    const byeCount = tc - branch.preliminary_rounds * 2;
    const byeTeams = allTeams.slice(0, byeCount);
    const prelimTeams = allTeams.slice(byeCount);

    const prelims: PlayoffMatch[] = [];
    for (let i = 0; i < branch.preliminary_rounds; i++) {
      const teamA = prelimTeams[i];
      const teamB = prelimTeams[prelimTeams.length - 1 - i] || prelimTeams[i + 1];
      prelims.push({
        id: `pk_${i + 1}`,
        round_key: 'preliminary',
        round_name: t('bracket.preliminary', { n: i + 1 }),
        team_a: teamA || null,
        team_b: teamB || null,
        series: branch.preliminary_series,
        winner_to: null,
        loser_to: `branch_${branch.id}_prelim_losers`,
        is_placeholder: true,
        branch_id: branch.id,
      });
    }
    matches.push(...prelims);

    const firstRk = roundKey(0, rounds.length);
    const qfRoundName = rounds[0]?.name || t('bracket.quarterfinal');
    const qfMatches: PlayoffMatch[] = [];
    for (let i = 0; i < byeTeams.length; i++) {
      const qfId = `qf_${i + 1}`;
      const linkedPrelim = prelims[prelims.length - 1 - i];
      qfMatches.push({
        id: qfId,
        round_key: firstRk,
        round_name: `${qfRoundName} ${i + 1}`,
        team_a: byeTeams[i],
        team_b: {
          id: null,
          name: t('bracket.winnerOf', { name: linkedPrelim?.round_name || '?' }),
          seed: 0, group: '', group_position: 0,
          role: 'prelim', is_placeholder: true,
        },
        series: rounds[0]?.series || 1,
        winner_to: null, loser_to: null,
        is_placeholder: true,
        branch_id: branch.id,
      });
      if (linkedPrelim) linkedPrelim.winner_to = qfId;
    }
    matches.push(...qfMatches);
    matches.push(...buildRemainingRounds(qfMatches, rounds, 1, branch.id));
  } else {
    // No preliminaries — straight bracket
    let prev: PlayoffMatch[] = [];
    const firstMatchCount = Math.ceil(allTeams.length / 2);
    const firstRk = roundKey(0, rounds.length);
    for (let mi = 0; mi < firstMatchCount; mi++) {
      const matchId = firstRk === 'final' ? 'final' : `${firstRk}_${mi + 1}`;
      prev.push({
        id: matchId,
        round_key: firstRk,
        round_name: `${rounds[0]?.name || t('bracket.roundN', { n: 1 })}${firstMatchCount > 1 ? ` ${mi + 1}` : ''}`,
        team_a: allTeams[mi * 2] || null,
        team_b: allTeams[mi * 2 + 1] || null,
        series: rounds[0]?.series || 1,
        winner_to: null, loser_to: null,
        is_placeholder: true,
        branch_id: branch.id,
      });
    }
    matches.push(...prev);
    matches.push(...buildRemainingRounds(prev, rounds, 1, branch.id));
  }

  linkLosers(matches, branch.id);
  return matches;
}

// ═══════════════════════════════════════════════════════
// Main entry point
// ═══════════════════════════════════════════════════════

export function generateBracket(
  branch: PlayoffBranch,
  rules: AdvancementRule[],
  groupResults: GroupResult[] | null,
  groupCount: number,
): PlayoffBracketResult {
  const branchRules = rules.filter(r => r.target.branch_id === branch.id);

  let matches: PlayoffMatch[];

  if (branchRules.length === 0) {
    // Direct playoff — no advancement rules, generate from branch config alone
    matches = generateDirectBracket(branch, groupCount);
  } else if (branchRules.some(r => r.pairing?.type === 'cross_group_pairs')) {
    const byeRule = branchRules.find(r => r.target.role === 'bye')!;
    const prelimRule = branchRules.find(r => r.target.role === 'prelim')!;
    matches = generateCrossGroupPairsBracket(branch, byeRule, prelimRule, groupCount, groupResults);
  } else {
    matches = generateStandardBracket(branch, branchRules, groupCount, groupResults);
  }

  // Post-process: resolve placeholder teams using groupResults (needed for generateDirectBracket)
  if (groupResults && groupResults.length > 0) {
    for (const match of matches) {
      if (match.team_a?.is_placeholder && match.team_a.group && match.team_a.group_position) {
        match.team_a = resolveTeam(match.team_a.group, match.team_a.group_position, match.team_a.role, match.team_a.seed, groupResults);
      }
      if (match.team_b?.is_placeholder && match.team_b.group && match.team_b.group_position) {
        match.team_b = resolveTeam(match.team_b.group, match.team_b.group_position, match.team_b.role, match.team_b.seed, groupResults);
      }
    }
  }

  return {
    branch_id: branch.id,
    branch_name: branch.name,
    matches,
  };
}
